/* Tasks — STORY-025 and STORY-026.
 *
 * ── the default view is opinionated, and that is the feature ────────────────
 * Mine, and not finished. A spreadsheet has no view layer, and past a few dozen
 * rows finding your own work means reading all of it — which is the point at
 * which people stop reading it and keep a private list instead. So there is no
 * "show only mine" control: that is what you already see, and clearing the
 * assignee is how you leave it.
 *
 * ── and its cost is paid out loud ───────────────────────────────────────────
 * Prototyping found the cost: an overdue High-priority Task belonging to a
 * colleague is invisible on the screen whose entire job is telling you what
 * needs doing. Decided 2026-08-14 — keep the default, and **say the number**.
 * A line naming what the filter is hiding costs nothing on the common day when
 * it hides nothing.
 *
 * ── overdue is the reader's day, and it is said in words ────────────────────
 * The zone travels with the request because only the client knows where the
 * reader is standing. On screen, being late is carried by an ordinary word and
 * by position at the top of the list — never by colour, which `design.md`
 * forbids alone and which a greyscale screen and a screen reader both miss.
 */

import {
  EVENT_STATUSES,
  EVENTS_PAGE_MAX,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TASKS_PAGE_SIZE,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '@eventuley/contracts';
import { isOverdue } from '@eventuley/domain';
import { formatDate, type MessageKey } from '@eventuley/i18n';
import { useParams } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useEvents } from '../../events/index.js';
import { useMembers } from '../../organisations/index.js';
import { useCreateTask, useDeleteTask, useTasks, useUpdateTask } from '../api/index.js';

/* Explicit maps, never a constructed key — `i18n.md` forbids one because it
 * defeats extraction and unused-key detection. */
const STATUS_LABELS = {
  open: 'tasks.status.open',
  in_progress: 'tasks.status.in_progress',
  completed: 'tasks.status.completed',
  cancelled: 'tasks.status.cancelled',
} as const satisfies Record<TaskStatus, MessageKey>;

const PRIORITY_LABELS = {
  low: 'tasks.priority.low',
  medium: 'tasks.priority.medium',
  high: 'tasks.priority.high',
} as const satisfies Record<TaskPriority, MessageKey>;

const UNFINISHED = ['open', 'in_progress'] as const satisfies readonly TaskStatus[];

const EMPTY_DRAFT = {
  description: '',
  assigneeId: '',
  eventId: '',
  startsOn: '',
  dueOn: '',
  priority: 'medium' as TaskPriority,
};

export function TasksScreen() {
  const t = useTranslate();
  const { organisationId } = useParams({ strict: false }) as { organisationId: string };
  const { formatLocale: locale } = useI18n();

  /* The reader's own zone, from the runtime. Never a stored Organisation
   * default: "due today" is the day where the READER is, and two Members in
   * two zones are allowed to differ by a day — decided 2026-08-12. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [mineOnly, setMineOnly] = useState(true);
  const [statuses, setStatuses] = useState<readonly TaskStatus[]>(UNFINISHED);
  const [priorities, setPriorities] = useState<readonly TaskPriority[]>([]);
  const [page, setPage] = useState(1);
  const [writing, setWriting] = useState(false);

  /* An empty status list means "none of them", and the server reads an absent
   * one as its default of open-and-in-progress. Sending nothing would answer a
   * reader who unticked every box with a full list and no box ticked — so the
   * screen answers it here instead of asking. The events feature does the same
   * thing for the same reason. */
  const wantsNothing = statuses.length === 0;

  const query = {
    ...(mineOnly ? {} : { assigneeId: 'all' as const }),
    status: [...statuses],
    ...(priorities.length > 0 ? { priority: [...priorities] } : {}),
    timeZone,
    limit: TASKS_PAGE_SIZE,
    offset: (page - 1) * TASKS_PAGE_SIZE,
  };

  const { data, error, isLoading } = useTasks(organisationId, query, { enabled: !wantsNothing });
  const { data: memberData } = useMembers(organisationId);

  const filtersActive =
    !mineOnly ||
    priorities.length > 0 ||
    statuses.length !== UNFINISHED.length ||
    !UNFINISHED.every((status) => statuses.includes(status));

  /* Back to the default view. Used by the empty state, where the filters are
   * genuinely narrower than the default and resetting is what the reader
   * wants. */
  const clearFilters = () => {
    setMineOnly(true);
    setStatuses(UNFINISHED);
    setPriorities([]);
    setPage(1);
  };

  /**
   * Wide enough to show what was hidden — which is NOT the default.
   *
   * The hidden-overdue callout appears in the default state more often than
   * anywhere else, because the default is what hides a colleague's overdue
   * work. Wiring its button to `clearFilters` made it a no-op in exactly that
   * case: every setter was handed the value it already had, the query key
   * hashed identically, nothing refetched, and the banner stayed up while the
   * reader clicked it. It has to widen.
   */
  const showEverything = () => {
    setMineOnly(false);
    setStatuses(UNFINISHED);
    setPriorities([]);
    setPage(1);
  };

  if (error) {
    return (
      <>
        <div className="page-head">
          <h1>{t('tasks.title')}</h1>
        </div>
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      </>
    );
  }

  const tasks = wantsNothing ? [] : (data?.tasks ?? []);
  const total = wantsNothing ? 0 : (data?.total ?? 0);
  const lastPage = Math.max(1, Math.ceil(total / TASKS_PAGE_SIZE));

  return (
    <>
      <div className="page-head">
        <h1>{t('tasks.title')}</h1>
        <Button variant="primary" onClick={() => setWriting(!writing)}>
          {writing ? t('tasks.close_form') : t('tasks.new')}
        </Button>
      </div>

      {writing ? (
        <TaskForm
          organisationId={organisationId}
          members={memberData?.members ?? []}
          onDone={() => setWriting(false)}
        />
      ) : null}

      <Filters
        mineOnly={mineOnly}
        statuses={statuses}
        priorities={priorities}
        onMine={(value) => {
          setMineOnly(value);
          setPage(1);
        }}
        onStatuses={(value) => {
          setStatuses(value);
          setPage(1);
        }}
        onPriorities={(value) => {
          setPriorities(value);
          setPage(1);
        }}
      />

      {/* The cost of the opinionated default, said out loud. Rendered only when
          there is something to say, which on the common day is never. */}
      {(data?.hiddenOverdue ?? 0) > 0 ? (
        <Callout tone="warning">
          <p>{t('tasks.hidden_overdue', { count: data?.hiddenOverdue ?? 0 })}</p>
          <div className="btn-row">
            <Button variant="secondary" onClick={showEverything}>
              {t('tasks.show_hidden')}
            </Button>
          </div>
        </Callout>
      ) : null}

      {isLoading && !wantsNothing && tasks.length === 0 ? (
        <p className="field__hint">{t('common.loading')}</p>
      ) : tasks.length === 0 ? (
        /* Never a blank page. If filters are on, say so and offer to clear
           them — the reader cannot tell an empty workspace from an over-narrow
           filter, and guessing wrong wastes their afternoon. */
        <Callout tone="info">
          <p>{filtersActive ? t('tasks.none_match') : t('tasks.none_yet')}</p>
          {filtersActive ? (
            <div className="btn-row">
              <Button variant="secondary" onClick={clearFilters}>
                {t('tasks.clear_filters')}
              </Button>
            </div>
          ) : null}
        </Callout>
      ) : (
        <>
          <ul className="row-list">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                organisationId={organisationId}
                timeZone={timeZone}
                locale={locale}
              />
            ))}
          </ul>

          {total > TASKS_PAGE_SIZE ? (
            <div className="filters">
              <p className="field__hint" role="status">
                {t('tasks.showing_page', {
                  from: (page - 1) * TASKS_PAGE_SIZE + 1,
                  to: Math.min(page * TASKS_PAGE_SIZE, total),
                  total,
                  page,
                  pages: lastPage,
                })}
              </p>
              <div className="btn-row">
                <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  {t('tasks.previous')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={page >= lastPage}
                  onClick={() => setPage(page + 1)}
                >
                  {t('tasks.next')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

function Filters({
  mineOnly,
  statuses,
  priorities,
  onMine,
  onStatuses,
  onPriorities,
}: {
  readonly mineOnly: boolean;
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly onMine: (value: boolean) => void;
  readonly onStatuses: (value: readonly TaskStatus[]) => void;
  readonly onPriorities: (value: readonly TaskPriority[]) => void;
}) {
  const t = useTranslate();
  const assigneeId = useId();

  const toggle = <T,>(list: readonly T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  /* A section rather than a div: `aria-label` needs a role to attach to, and a
   * landmark is the honest one for a group of controls that narrow the list
   * beneath it. */
  return (
    <section className="filters" aria-label={t('tasks.filters_label')}>
      <div className="field">
        <label className="field__label" htmlFor={assigneeId}>
          {t('tasks.assignee_filter_label')}
        </label>
        <select
          id={assigneeId}
          className="field__control"
          value={mineOnly ? 'mine' : 'all'}
          onChange={(changed) => onMine(changed.target.value === 'mine')}
        >
          {/* "Mine" first because it is the default, and named rather than
              implied — a control whose current state is invisible is one the
              reader cannot reason about. */}
          <option value="mine">{t('tasks.assignee_mine')}</option>
          <option value="all">{t('tasks.assignee_everyone')}</option>
        </select>
      </div>

      <fieldset className="filters__group">
        <legend className="field__label">{t('tasks.status_filter_label')}</legend>
        {TASK_STATUSES.map((status) => (
          <label className="filters__check" key={status}>
            <input
              type="checkbox"
              checked={statuses.includes(status)}
              onChange={() => onStatuses(toggle(statuses, status))}
            />
            {t(STATUS_LABELS[status])}
          </label>
        ))}
      </fieldset>

      <fieldset className="filters__group">
        <legend className="field__label">{t('tasks.priority_filter_label')}</legend>
        {TASK_PRIORITIES.map((priority) => (
          <label className="filters__check" key={priority}>
            <input
              type="checkbox"
              checked={priorities.includes(priority)}
              onChange={() => onPriorities(toggle(priorities, priority))}
            />
            {t(PRIORITY_LABELS[priority])}
          </label>
        ))}
      </fieldset>
    </section>
  );
}

function TaskRow({
  task,
  organisationId,
  timeZone,
  locale,
}: {
  readonly task: Task;
  readonly organisationId: string;
  readonly timeZone: string;
  readonly locale: string;
}) {
  const t = useTranslate();
  const update = useUpdateTask(organisationId);
  const remove = useDeleteTask(organisationId);
  const [confirming, setConfirming] = useState(false);
  const statusId = useId();

  const late = isOverdue(task, timeZone, new Date());
  const stale = update.error instanceof ApiError && update.error.status === 409;

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">
          {task.description}
          {/* In WORDS, and it leads the row. `design.md` forbids colour alone,
              and a red dot is invisible to a greyscale screen and to a screen
              reader alike. */}
          {late ? <span className="badge badge--danger">{t('tasks.overdue')}</span> : null}
        </span>
        <span className="row__meta">
          {t('tasks.row_meta', {
            assignee: task.assignee.anonymised
              ? t('tasks.assignee_erased')
              : task.assignee.displayName,
            priority: t(PRIORITY_LABELS[task.priority]),
          })}
        </span>
        {task.dueOn ? (
          <span className="row__meta">
            {/* Formatted, never printed raw — `i18n.md` hard rule 3. Read in
                UTC because the value is a calendar DATE: parsing it as a local
                instant would shift it a day for anybody west of the meridian. */}
            {t('tasks.due', {
              date: formatDate(new Date(`${task.dueOn}T00:00:00Z`), { locale, timeZone: 'UTC' }),
            })}
          </span>
        ) : null}
        {task.event ? <span className="row__meta">{task.event.name}</span> : null}
        {/* An assignee who has left is stated, not left as a name that no
            longer means anything. The work may still need doing. */}
        {task.assignee.member ? null : (
          <span className="row__meta">{t('tasks.assignee_left')}</span>
        )}
      </span>

      <span className="row__actions">
        <div className="field">
          <label className="field__label sr-only" htmlFor={statusId}>
            {t('tasks.status_label')}
          </label>
          <select
            id={statusId}
            className="field__control"
            value={task.status}
            disabled={update.isPending}
            onChange={(changed) =>
              update.mutate({
                taskId: task.id,
                status: changed.target.value as TaskStatus,
                /* What the reader believed when they started. Without it the
                   second write silently overwrites the first, where the story
                   asks that one wins and the other is told. */
                expectedUpdatedAt: task.updatedAt,
              })
            }
          >
            {TASK_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(STATUS_LABELS[status])}
              </option>
            ))}
          </select>
        </div>
        <Button variant="ghost" onClick={() => setConfirming(true)}>
          {t('tasks.delete')}
        </Button>
      </span>

      {stale ? (
        <div className="row__expand">
          {/* The list is already refetching — a stale write invalidates the
              same prefix a successful one does, so the row underneath this
              message corrects itself rather than sitting on the value the
              reader tried to save. */}
          <Callout tone="warning">{t('tasks.changed_underneath')}</Callout>
        </div>
      ) : update.error ? (
        <div className="row__expand">
          <Callout tone="danger">
            {update.error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
          </Callout>
        </div>
      ) : null}

      {confirming ? (
        <div className="row__expand">
          <Callout tone="danger">
            <strong>{t('tasks.delete_confirm_title', { description: task.description })}</strong>
            <p>{t('tasks.delete_confirm_body')}</p>
            <div className="btn-row">
              <Button
                variant="secondary"
                busy={remove.isPending}
                onClick={() => remove.mutate(task.id)}
              >
                {t('tasks.delete_confirm_submit')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                  remove.reset();
                }}
              >
                {t('tasks.delete_confirm_cancel')}
              </Button>
            </div>
          </Callout>
        </div>
      ) : null}
    </li>
  );
}

function TaskForm({
  organisationId,
  members,
  onDone,
}: {
  readonly organisationId: string;
  readonly members: readonly { userId: string; displayName: string }[];
  readonly onDone: () => void;
}) {
  const t = useTranslate();
  const create = useCreateTask(organisationId);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  /* The far-future question, held until it is answered. A second endpoint
     would be two code paths, and the one nobody exercises is the one missing a
     rule — so the confirmation is a flag on the same request. */
  const [confirmFarFuture, setConfirmFarFuture] = useState(false);

  const assigneeId = useId();
  const eventId = useId();
  const priorityId = useId();

  /* Every status and as many as the endpoint gives, newest first. The default
   * is three statuses and the first 25 by date ASCENDING, so "chase the final
   * invoice for last month's wedding" could not find the wedding: it is
   * completed, and it is not among the earliest. The same defect STORY-022's
   * review found on the money form, in the same shape. */
  const { data: eventData } = useEvents(organisationId, {
    status: [...EVENT_STATUSES],
    sort: 'date',
    direction: 'desc',
    limit: EVENTS_PAGE_MAX,
  });

  const farFuture =
    create.error instanceof ApiError && create.error.code === 'confirmation_required';

  const submit = () => {
    create.mutate(
      {
        description: draft.description,
        assigneeId: draft.assigneeId,
        ...(draft.eventId === '' ? {} : { eventId: draft.eventId }),
        ...(draft.startsOn === '' ? {} : { startsOn: draft.startsOn }),
        ...(draft.dueOn === '' ? {} : { dueOn: draft.dueOn }),
        priority: draft.priority,
        ...(confirmFarFuture ? { confirmFarFuture: true } : {}),
      },
      {
        onSuccess: () => {
          setDraft(EMPTY_DRAFT);
          setConfirmFarFuture(false);
          onDone();
        },
      },
    );
  };

  return (
    <form
      className="card"
      onSubmit={(submitted) => {
        submitted.preventDefault();
        submit();
      }}
    >
      <TextField
        label={t('tasks.description_label')}
        value={draft.description}
        required
        onChange={(changed) => setDraft({ ...draft, description: changed.target.value })}
      />

      <div className="field">
        <label className="field__label" htmlFor={assigneeId}>
          {t('tasks.assignee_label')}
        </label>
        <select
          id={assigneeId}
          className="field__control"
          value={draft.assigneeId}
          required
          onChange={(changed) => setDraft({ ...draft, assigneeId: changed.target.value })}
        >
          {/* No blank option that saves. A Task belongs to somebody — an
              "unassigned" bucket was considered and rejected on 2026-08-14,
              because it lets every Task belong to nobody. */}
          <option value="">{t('tasks.assignee_choose')}</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={priorityId}>
          {t('tasks.priority_label')}
        </label>
        <select
          id={priorityId}
          className="field__control"
          value={draft.priority}
          onChange={(changed) =>
            setDraft({ ...draft, priority: changed.target.value as TaskPriority })
          }
        >
          {TASK_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {t(PRIORITY_LABELS[priority])}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={eventId}>
          {t('tasks.event_label')}
        </label>
        <select
          id={eventId}
          className="field__control"
          value={draft.eventId}
          onChange={(changed) => setDraft({ ...draft, eventId: changed.target.value })}
        >
          {/* Business work is FIRST and it is named. Most Tasks have no Event,
              and a blank option reads as one somebody forgot to fill. */}
          <option value="">{t('tasks.event_none')}</option>
          {(eventData?.events ?? []).map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
      </div>

      <TextField
        label={t('tasks.starts_label')}
        type="date"
        value={draft.startsOn}
        onChange={(changed) => setDraft({ ...draft, startsOn: changed.target.value })}
      />
      <TextField
        label={t('tasks.due_label')}
        type="date"
        value={draft.dueOn}
        onChange={(changed) => setDraft({ ...draft, dueOn: changed.target.value })}
      />

      {farFuture ? (
        <Callout tone="warning">
          <p>{t('tasks.far_future_warning')}</p>
          <div className="btn-row">
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmFarFuture(true);
                create.reset();
                /* Re-sent immediately with the confirmation, so the reader
                   answers the question once rather than re-submitting a form
                   they have already filled in. */
                create.mutate({
                  description: draft.description,
                  assigneeId: draft.assigneeId,
                  ...(draft.eventId === '' ? {} : { eventId: draft.eventId }),
                  ...(draft.startsOn === '' ? {} : { startsOn: draft.startsOn }),
                  ...(draft.dueOn === '' ? {} : { dueOn: draft.dueOn }),
                  priority: draft.priority,
                  confirmFarFuture: true,
                });
              }}
            >
              {t('tasks.far_future_confirm')}
            </Button>
          </div>
        </Callout>
      ) : create.error ? (
        <Callout tone="danger">
          {create.error instanceof NetworkError
            ? t('error.network')
            : create.error instanceof ApiError && create.error.status === 400
              ? t('tasks.refused')
              : t('error.unexpected')}
        </Callout>
      ) : null}

      <div className="btn-row">
        <Button variant="primary" type="submit" busy={create.isPending}>
          {t('tasks.submit')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('tasks.cancel')}
        </Button>
      </div>
    </form>
  );
}
